import React from 'react';
import { noop, sortBy } from 'lodash';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Icon, { GLYPHS } from '../../../../common/icon';
import AddTranslationButton from './AddTranslationButton';

const supportedLanguages = [
  {
    value: 'en',
    description: 'English',
  },
  {
    value: 'es-419',
    description: 'Español',
  },
  {
    value: 'fr',
    description: 'Français',
  },
];

const LocalizationTabs = ({
  children,
  selectedLanguagesCodes,
  supportedLanguagesCodes,
  currentLanguageCode,
  defaultLanguageCode,
  onAddLanguage,
  onRemoveLanguage,
  onChangeCurrentLanguage,
}) => {
  const availableLanguages = supportedLanguagesCodes
    .map((languageCode) => supportedLanguages.find(({ value }) => value === languageCode))
    .filter(Boolean)
    .map((language) => ({
      ...language,
      isDefault: language.value === defaultLanguageCode,
    }));

  const restLanguages = availableLanguages.filter(({ value }) =>
    selectedLanguagesCodes.every((code) => code !== value)
  );

  const selectedLanguages = sortBy(
    selectedLanguagesCodes
      .map((languageCode) => availableLanguages.find(({ value }) => value === languageCode))
      .filter(Boolean),
    (language) => (defaultLanguageCode === language.value ? -1 : 1) // default language first
  );

  return (
    <div className="c-localization-tabs">
      <div className="c-localization-tabs__tabs-area">
        {selectedLanguages.map((language) => (
          <div
            key={language.value}
            className={classnames('c-localization-tabs__tab', {
              'c-localization-tabs__tab_active': currentLanguageCode === language.value,
            })}
          >
            <button
              className="c-localization-tabs__language-button"
              type="button"
              onClick={() => onChangeCurrentLanguage(language.value)}
            >
              <Icon glyph={GLYPHS.languageGlobe} />
              {language.description}
            </button>
            {!language.isDefault && (
              <button
                className="c-localization-tabs__remove-language-button"
                type="button"
                onClick={() => {
                  onRemoveLanguage(language.value);
                  const remainingLanguages = selectedLanguages.filter((lang) => lang.value !== language.value);
                  const firstLanguage = remainingLanguages[0];
                  if (firstLanguage) {
                    onChangeCurrentLanguage(firstLanguage.value);
                  }
                }}
              >
                <Icon glyph={GLYPHS.crossSign} size="micro" />
              </button>
            )}
          </div>
        ))}
        {!!restLanguages.length && (
          <AddTranslationButton
            languages={restLanguages}
            onAdd={(language) => {
              onAddLanguage(language.value);
              onChangeCurrentLanguage(language.value);
            }}
          />
        )}
      </div>
      <div className="c-localization-tabs__main-area">{children}</div>
    </div>
  );
};

LocalizationTabs.defaultProps = {
  selectedLanguagesCodes: [],
  supportedLanguagesCodes: [],
  onAddLanguage: noop,
  onRemoveLanguage: noop,
  onChangeCurrentLanguage: noop,
  children: null,
};

LocalizationTabs.propTypes = {
  selectedLanguagesCodes: PropTypes.arrayOf(PropTypes.string),
  supportedLanguagesCodes: PropTypes.arrayOf(PropTypes.string),
  currentLanguageCode: PropTypes.string.isRequired,
  defaultLanguageCode: PropTypes.string.isRequired,
  onAddLanguage: PropTypes.func,
  onRemoveLanguage: PropTypes.func,
  onChangeCurrentLanguage: PropTypes.func,
  children: PropTypes.node,
};

export default LocalizationTabs;
